import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { getActiveToken } from '../utils/session';
import { BUSINESS } from '../utils/brand';
import './PaymentCancelled.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

/**
 * /payment/cancelled — where Stripe sends a customer who backs out of checkout.
 *
 * The booking was created BEFORE checkout opened, so leaving Stripe does not
 * undo it. Until 2026-09-16 this URL was unrouted and the customer landed on
 * "Page not found" with a booking already on the calendar — the moment they
 * decide whether to call or disappear.
 *
 * Policy (Thomas, 2026-09-16): payment is never a barrier to BOOKING; it is a
 * barrier to DELIVERY. The gallery payment gate enforces that, so this page
 * says plainly that the shoot stands and the photos unlock once the invoice is
 * paid. Every fetch here soft-defaults: with no booking details the page still
 * reads correctly.
 */
function PaymentCancelled() {
  const [searchParams] = useSearchParams();
  const bookingId = searchParams.get('booking_id') || '';
  // Guest checkout (Pack B2): present when a GUEST backed out of Stripe. Their
  // booking is a 30-minute hold that is only confirmed by payment.
  const holdToken = searchParams.get('hold') || '';
  const [booking, setBooking] = useState(null);
  const [reopening, setReopening] = useState(false);
  const [holdError, setHoldError] = useState('');
  const [holdExpired, setHoldExpired] = useState(false);

  const reopenCheckout = async () => {
    setReopening(true);
    setHoldError('');
    try {
      const res = await axios.post(`${API_URL}/payment/create-checkout-session`, { bookingId, guestToken: holdToken });
      if (res.data?.url) { window.location.href = res.data.url; return; }
      setHoldError('Online payment is unavailable right now. Please call us to finish booking.');
    } catch (e) {
      if (e.response?.status === 410) setHoldExpired(true);
      else setHoldError(e.response?.data?.message || 'Online payment is unavailable right now. Please call us to finish booking.');
    }
    setReopening(false);
  };

  useEffect(() => {
    const token = getActiveToken();
    if (!bookingId || !token) return;
    let cancelled = false;
    axios.get(`${API_URL}/booking/my-bookings`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => {
        const list = res.data?.bookings || res.data?.data || [];
        const match = Array.isArray(list) ? list.find(b => String(b._id) === String(bookingId)) : null;
        if (!cancelled && match) setBooking(match);
      })
      .catch(() => { /* details are a nicety; the message stands without them */ });
    return () => { cancelled = true; };
  }, [bookingId]);

  const when = booking?.appointmentDate
    ? new Date(booking.appointmentDate).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', timeZone: 'UTC' })
    : null;
  const total = Number(booking?.totalPrice);
  const tel = `tel:${BUSINESS.phone.replace(/[^0-9+]/g, '')}`;

  if (holdToken) {
    return (
      <div className="payment-cancelled-page">
        <div className="pc-inner">
          {holdExpired ? (
            <>
              <h1 className="pc-title">That time slot was released.</h1>
              <p className="pc-lede">Checkout wasn't completed within 30 minutes, so the time went back on the calendar. Nothing was charged.</p>
              <div className="pc-actions">
                <Link to="/book" className="btn btn-primary">Book again</Link>
              </div>
            </>
          ) : (
            <>
              <h1 className="pc-title">Your time is held — for now.</h1>
              <p className="pc-lede">Checkout closed before payment, so nothing was charged and the booking isn't confirmed yet.</p>
              <p className="pc-body">
                As a guest booking, your slot is held for 30 minutes from when checkout opened. Pay within that window to confirm it; after that it's released for someone else.
              </p>
              <div className="pc-actions">
                <button type="button" className="btn btn-primary" onClick={reopenCheckout} disabled={reopening}>
                  {reopening ? 'Opening secure checkout…' : 'Return to secure checkout'}
                </button>
              </div>
              {holdError && <p className="pc-body" role="alert">{holdError}</p>}
            </>
          )}
          <p className="pc-help">
            Questions or trouble paying? Call <a href={tel}>{BUSINESS.phone}</a>.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="payment-cancelled-page">
      <div className="pc-inner">
        <h1 className="pc-title">Your shoot is still booked.</h1>
        <p className="pc-lede">
          Checkout closed before payment went through, so nothing was charged.
        </p>

        {booking && (
          <dl className="pc-booking" aria-label="Your booking">
            {when && (<><dt>Date</dt><dd>{when}{booking.appointmentTime ? `, ${booking.appointmentTime}` : ''}</dd></>)}
            {booking.propertyAddress && (<><dt>Property</dt><dd>{booking.propertyAddress}</dd></>)}
            {booking.package && (<><dt>Package</dt><dd className="pc-cap">{booking.package}</dd></>)}
            {Number.isFinite(total) && total > 0 && (<><dt>Total</dt><dd>${total.toFixed(2)}</dd></>)}
          </dl>
        )}

        <p className="pc-body">
          You can pay now or any time before your photos are delivered. The gallery
          unlocks automatically as soon as the invoice is paid.
        </p>

        <div className="pc-actions">
          <Link to="/client/portal?tab=invoices" className="btn btn-primary">Pay the invoice now</Link>
          <Link to={bookingId ? `/booking/confirmed/${bookingId}` : '/client/portal'} className="btn btn-outline">
            Pay later
          </Link>
        </div>

        <p className="pc-help">
          Card declined, or a question about the amount? Call <a href={tel}>{BUSINESS.phone}</a>.
        </p>
      </div>
    </div>
  );
}

export default PaymentCancelled;
